import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import styled from 'foundations-theming/utils';
import PublicContentCard from '../../publicPresentation/components/PublicContentCard';
import usageTracker from '../../../utils/usageTracker';
import { getProxyUrl } from 'academy-common-lib/utils/url';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { NavMarker } from 'react-rhumb';
import ACBadRequestError from 'academy-common-lib/components/error/ACBadRequestError';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import DocumentHead from '../../publicSeo/components/DocumentHead';
import { usePagination } from '../publicCatalogPaginationHooks';
import Schema from '../../publicSeo/components/Schema';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import { useGetCatalogResultsFromUrlFilters } from '../publicCatalogResultsHooks';
import { useRef } from 'react';
import { getSignupUrl } from '../../../utils/urls';
import { useIsLoggedIn } from '../../publicLayout/publicQueries';
import { ThemedLinkMixin } from '../../../components/ThemedComponents';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ThemedPaginator = styled.div.withConfig({
  displayName: "PublicCatalogResults__ThemedPaginator",
  componentId: "q0wedu-0"
})(() => ({
  button: Object.assign({}, ThemedLinkMixin(), {
    // temporary CSS override to ensure the paginator button labels are aligned on a single line within mobile viewports
    //Related slack thread: https://hubspot.slack.com/archives/C1WG3L3J6/p1748360997810479
    width: 'min-content',
    margin: '0 !important'
  })
}));
export const PAGE_SIZE = 12;
const getCardContentType = (contentType, hasAward) => {
  if (hasAward) return 'CERTIFICATION';
  if (contentType === 'TRACK') return 'COURSE';
  if (contentType === 'LESSON') return 'LESSON';
  return null;
};
const getResultHrefs = (result, isLoggedIn, language) => {
  const modalOverviewParams = getOverviewModalParams({
    id: result.id,
    type: result.contentType === 'LESSON' ? 'LESSON' : 'TRACK',
    source: 'public-catalog',
    language: language.toUpperCase()
  });
  const internalOverviewHref = `https://app.hubspot.com/academy/l/?${new URLSearchParams(modalOverviewParams)}`;
  const internalSignupHref = getSignupUrl(result.contentType === 'LESSON' ? 'LESSON' : 'COURSE', result.id);
  const externalPath = `/${result.contentType === 'LESSON' ? 'lessons' : 'courses'}/${result.pagePath}`;
  const externalHref = getProxyUrl(externalPath, language);
  return {
    href: result.pagePath ? externalHref : internalOverviewHref,
    to: result.pagePath ? externalPath : undefined,
    footerHref: isLoggedIn ? internalOverviewHref : internalSignupHref
  };
};
const ResultsSchema = ({
  resultsData,
  language
}) => {
  var _resultsData$results;
  if (!(resultsData !== null && resultsData !== void 0 && (_resultsData$results = resultsData.results) !== null && _resultsData$results !== void 0 && _resultsData$results.length)) return null;
  return /*#__PURE__*/_jsx(DocumentHead, {
    children: /*#__PURE__*/_jsx(Schema, {
      object: {
        '@type': 'ItemList',
        numberOfItems: resultsData.total,
        itemListElement: resultsData.results.map((result, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: getResultHrefs(result, false, language).href,
          name: result.name
        }))
      }
    })
  });
};
export const PublicCatalogResultsDisplay = ({
  data,
  error,
  loading,
  language,
  disablePaginator = false,
  disableSchema = false
}) => {
  var _error$status, _data$results;
  const {
    isMobile
  } = useMediaQuery();
  const [pageNumber, setPageNumber] = usePagination(true);
  const pageCount = useRef(0);
  const {
    isLoggedIn
  } = useIsLoggedIn();
  const getResultCardClickHandler = (name, href, index, type) => () => {
    usageTracker.track('interaction', {
      action: 'catalog-results-item-click',
      name,
      href,
      index: index + 1,
      type
    });
  };
  const handlePageChange = ({
    target: {
      value
    }
  }) => {
    setPageNumber(value);
    if (window.scrollTo) window.scrollTo(0, 0);
    usageTracker.track('interaction', {
      action: 'catalog-results-change-page',
      value
    });
  };
  if (!loading && (error || !data)) return /*#__PURE__*/_jsxs(NavMarker, {
    name: "CATALOG_PAGE_LOAD_FAILURE",
    children: [/*#__PURE__*/_jsx(ACBadRequestError, {}), /*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx("meta", {
        content: (error === null || error === void 0 || (_error$status = error.status) === null || _error$status === void 0 ? void 0 : _error$status.toString()) || '500',
        name: "prerender-status-code"
      })
    }), window.prerenderReady = true]
  });
  if (!loading && data) {
    pageCount.current = data.total && Math.ceil(data.total / PAGE_SIZE) || 0;
  }
  const displayPaginator = !!(pageCount.current > 1) && !disablePaginator;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [loading ? /*#__PURE__*/_jsx("div", {
      style: {
        height: 1700
      },
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "catalog-loading",
        children: /*#__PURE__*/_jsx(LoadingDots, {
          minHeight: 100
        })
      })
    }) : data !== null && data !== void 0 && (_data$results = data.results) !== null && _data$results !== void 0 && _data$results.length ? /*#__PURE__*/_jsxs(NavMarker, {
      name: "CATALOG_RESULTS_LOADED",
      children: [!disableSchema && /*#__PURE__*/_jsx(ResultsSchema, {
        language: language,
        resultsData: data
      }), /*#__PURE__*/_jsxs(_Fragment, {
        children: [window.prerenderReady = true, /*#__PURE__*/_jsx(CardGrid, {
          columnGap: "800",
          columnSize: "240px",
          justifyItems: "center",
          marginBlockEnd: "600",
          rowGap: isMobile ? '800' : '1100',
          children: data.results.map((result, index) => {
            const {
              href,
              to,
              footerHref
            } = getResultHrefs(result, isLoggedIn, language);
            const cardContentType = getCardContentType(result.contentType, result.hasAward);
            if (!cardContentType) return null;
            return (
              /*#__PURE__*/
              // @bend-expect trellis-tokenized
              _jsx(PublicContentCard, {
                badge: result.seoBadge,
                categories: result.categories
                // @ts-expect-error ...restProps is used here
                ,
                className: "m-all-0",
                description: result.description || '',
                duration: result.duration,
                footerHref: footerHref,
                href: href,
                name: result.name || '',
                numberOfLessons: result.numberOfLessons,
                onClick: getResultCardClickHandler(result.name || '', href, index, cardContentType),
                thumbnailUrl: result.previewThumbnailRectangleUrl,
                to: to,
                type: cardContentType
              }, result.id)
            );
          })
        })]
      })]
    }) : /*#__PURE__*/_jsxs(NavMarker, {
      name: "CATALOG_PAGE_LOAD_EMPTY",
      children: [window.prerenderReady = true, /*#__PURE__*/_jsx(ErrorMessage, {
        illustration: "empty-state-charts",
        marginBlock: "1300",
        marginInline: "auto",
        maxInlineSize: "380px",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.catalogPage.noResultsTitle"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.catalogPage.noResultsDescription"
        })
      })]
    }), displayPaginator &&
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(ThemedPaginator, {
      children: /*#__PURE__*/_jsx(Paginator, {
        maxVisiblePageButtons: isMobile ? 0 : 6,
        onPageChange: handlePageChange,
        page: isMobile ? 0 : pageNumber,
        pageCount: pageCount.current,
        showFirstLastButtons: true
      })
    })]
  });
};
const PublicCatalogResults = () => {
  const {
    publicCatalogData,
    publicCatalogError,
    publicCatalogLoading,
    language
  } = useGetCatalogResultsFromUrlFilters({
    pageSize: PAGE_SIZE
  });
  return /*#__PURE__*/_jsx(PublicCatalogResultsDisplay, {
    data: publicCatalogData,
    error: publicCatalogError,
    language: language,
    loading: publicCatalogLoading
  });
};
export default PublicCatalogResults;